import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { DrillPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useDrillPageAnswerMode } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.answer-mode";

export type DrillPageGlyphPromptGate = Tagged<{ text: string }, "DrillPageGlyphPromptGate">;

/**
 * Whether the card shows `DrillPageGlyphPrompt`, the in-card answer prompt, and its copy. `null` is
 * the designed hidden state.
 *
 * "Tap on the card to reveal" lives BELOW the card (the deck's action area) for every front, so
 * the in-card prompt only ever adds what the card itself offers: the optional typed answer.
 * Visibility is an explicit whitelist, so a kind with no branch renders NO prompt at all. A
 * sentence drill is deliberately absent: its affordance is its footer (type a syllable, ask for a
 * hint, reveal).
 */
export function useDrillPageGlyphPromptGate(): DrillPageGlyphPromptGate | null {
  const [drillTypeKind] = useContextSelectors(DrillPageContext, (c) => c.drillType.kind);
  const { isAnswerModeActive } = useDrillPageAnswerMode();
  const isPronunciationReview = drillTypeKind === "pronunciation";
  const isWordSentenceReview = drillTypeKind === "word-sentence";

  if (!isAnswerModeActive || !(isWordSentenceReview || isPronunciationReview)) {
    return null;
  }
  return createGate<DrillPageGlyphPromptGate>({
    text: isPronunciationReview
      ? "Or input the pinyin below (optional)"
      : "Or type your answer below (optional)",
  });
}
